import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import FirmaCanvasModal from '../components/FirmaCanvasModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const CAT_COLOR = { exterior: '#3498db', interior: '#e67e22', documentos: '#27ae60', equipamiento: '#8e44ad' };

const ESTADOS = [
  { key: 'ok', label: 'OK', bg: '#27ae60', color: '#fff' },
  { key: 'nf', label: 'NF', bg: '#e74c3c', color: '#fff' },
  { key: 'nt', label: 'NT', bg: '#95a5a6', color: '#fff' },
];

const normalizar = (e) => e === 'nok' ? 'nf' : e === 'na' ? 'nt' : (e || 'nt');

const s = {
  page: { minHeight: '100vh', background: '#f0f2f5', fontFamily: 'Arial, sans-serif' },
  header: { background: '#2c3e6e', padding: '16px 20px' },
  logo: { color: '#fff', fontSize: 18, fontWeight: 700, margin: 0 },
  sub: { color: 'rgba(255,255,255,0.75)', fontSize: 12, margin: '2px 0 0' },
  wrap: { maxWidth: 680, margin: '0 auto', padding: '20px 12px' },
  card: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: 20, marginBottom: 16 },
  secTitle: { fontSize: 14, fontWeight: 700, color: '#2c3e6e', marginBottom: 12, borderBottom: '2px solid #e9ecef', paddingBottom: 8 },
  row: { display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' },
  field: { flex: 1, minWidth: 140 },
  label: { fontSize: 10, color: '#7f8c8d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 },
  val: { fontSize: 13, color: '#2c3e50', fontWeight: 500 },
};

export default function FirmaChecklist() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [firmadoPor, setFirmadoPor] = useState('');
  const [firmaPreview, setFirmaPreview] = useState(null);
  const [modalFirma, setModalFirma] = useState(false);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/checklist/firma/${token}`)
      .then(r => {
        setData(r.data);
        const map = {};
        (r.data.checklist?.respuestas || []).forEach(item => {
          map[item.id] = { estado: normalizar(item.estado), observacion: item.observacion || '' };
        });
        setRespuestas(map);
      })
      .catch(() => setError('Checklist no encontrado o link inválido.'))
      .finally(() => setLoading(false));
  }, [token]);

  const setEstado = (id, estado) => {
    setRespuestas(prev => ({ ...prev, [id]: { ...prev[id], estado } }));
  };

  const setObs = (id, obs) => {
    setRespuestas(prev => ({ ...prev, [id]: { ...prev[id], observacion: obs } }));
  };

  const enviar = async () => {
    if (!firmadoPor.trim()) { alert('Por favor ingrese su nombre completo.'); return; }
    if (!firmaPreview) { alert('Por favor firme antes de confirmar.'); return; }
    setSaving(true);
    try {
      const respuestasArr = Object.entries(respuestas).map(([id, r]) => ({
        id: parseInt(id), estado: r.estado, observacion: r.observacion,
      }));
      await axios.post(`${API}/checklist/firma/${token}`, {
        firmado_por: firmadoPor,
        firma_data: firmaPreview,
        respuestas: respuestasArr,
      });
      setDone(true);
    } catch (e) { alert(e.response?.data?.error || 'Error al guardar firma'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
      Cargando...
    </div>
  );
  if (error) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e74c3c', fontSize: 16 }}>
      {error}
    </div>
  );
  if (done) return (
    <div style={s.page}>
      <div style={s.header}><h2 style={s.logo}>HSS Rastreo Satelital</h2></div>
      <div style={s.wrap}>
        <div style={{ ...s.card, textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
          <h2 style={{ color: '#27ae60', marginBottom: 8 }}>Checklist firmado</h2>
          <p style={{ color: '#7f8c8d' }}>Firmado por <strong>{firmadoPor}</strong>. El checklist fue registrado correctamente.</p>
        </div>
      </div>
    </div>
  );

  const { checklist, orden, vehiculo, cliente, already_signed } = data;

  const porCategoria = {};
  (checklist.respuestas || []).forEach(r => {
    if (!porCategoria[r.categoria]) porCategoria[r.categoria] = [];
    porCategoria[r.categoria].push(r);
  });

  const resumen = Object.values(respuestas).reduce(
    (acc, r) => { acc[r.estado] = (acc[r.estado] || 0) + 1; return acc; },
    { ok: 0, nf: 0, nt: 0 }
  );
  const total = (checklist.respuestas || []).length;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.logo}>HSS Rastreo Satelital</h2>
        <p style={s.sub}>Checklist {checklist.tipo === 'inicial' ? 'INICIAL' : 'FINAL'} — {orden?.tipo}</p>
      </div>

      <div style={s.wrap}>
        {/* Datos de la orden */}
        <div style={s.card}>
          <div style={{
            display: 'inline-block',
            background: checklist.tipo === 'inicial' ? '#2c3e6e' : '#27ae60',
            color: '#fff', borderRadius: 8, padding: '6px 20px',
            fontWeight: 700, fontSize: 15, marginBottom: 14, letterSpacing: '1px',
          }}>
            CHECKLIST {checklist.tipo?.toUpperCase()}
          </div>
          <div style={s.row}>
            <div style={s.field}><div style={s.label}>Orden</div><div style={s.val}>#{orden?.numero_orden || orden?.id} — {orden?.tipo}</div></div>
            <div style={s.field}><div style={s.label}>Técnico</div><div style={s.val}>{orden?.responsable || '—'}</div></div>
            <div style={s.field}>
              <div style={s.label}>Fecha</div>
              <div style={s.val}>
                {checklist.fecha_creacion
                  ? new Date(checklist.fecha_creacion).toLocaleDateString('es-PY', { day: '2-digit', month: '2-digit', year: 'numeric' })
                  : '—'}
              </div>
            </div>
          </div>
          <div style={s.row}>
            <div style={s.field}><div style={s.label}>Vehículo</div><div style={s.val}>{vehiculo?.marca} {vehiculo?.modelo}</div></div>
            <div style={s.field}><div style={s.label}>Chapa</div><div style={{ ...s.val, fontFamily: 'monospace', fontSize: 15, fontWeight: 700 }}>{vehiculo?.chapa}</div></div>
            <div style={s.field}><div style={s.label}>Cliente</div><div style={s.val}>{cliente?.nombre}</div></div>
          </div>
        </div>

        {/* Resumen */}
        <div style={{ ...s.card, padding: '14px 20px' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 600 }}>RESUMEN ({total} ítems)</span>
            <span style={{ background: '#27ae60', color: '#fff', borderRadius: 20, padding: '3px 14px', fontWeight: 700, fontSize: 13 }}>{resumen.ok} OK</span>
            <span style={{ background: '#e74c3c', color: '#fff', borderRadius: 20, padding: '3px 14px', fontWeight: 700, fontSize: 13 }}>{resumen.nf} NF</span>
            <span style={{ background: '#95a5a6', color: '#fff', borderRadius: 20, padding: '3px 14px', fontWeight: 700, fontSize: 13 }}>{resumen.nt} NT</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: '#95a5a6' }}>
            OK = Funciona · NF = No funciona · NT = No tiene
          </div>
          {resumen.nf > 0 && (() => {
            const nfItems = (checklist.respuestas || []).filter(item => {
              const rv = respuestas[item.id];
              return rv ? rv.estado === 'nf' : normalizar(item.estado) === 'nf';
            });
            return (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#e74c3c', marginBottom: 6, letterSpacing: '0.5px' }}>NO FUNCIONA:</div>
                {nfItems.map(item => {
                  const obs = respuestas[item.id]?.observacion;
                  return (
                    <div key={item.id} style={{ fontSize: 12, color: '#c0392b', marginBottom: 4, display: 'flex', gap: 6 }}>
                      <span style={{ fontWeight: 700, flexShrink: 0 }}>•</span>
                      <span>{item.descripcion}{obs ? <span style={{ color: '#7f8c8d' }}> — {obs}</span> : null}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Items por categoría */}
        <div style={s.card}>
          <div style={s.secTitle}>Estado de los ítems</div>
          {Object.entries(porCategoria).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: CAT_COLOR[cat] || '#636e72', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
                {cat}
              </div>
              {items.map(item => {
                const rv = respuestas[item.id] || { estado: 'nt', observacion: '' };
                return (
                  <div key={item.id} style={{
                    padding: '10px 12px', marginBottom: 8, borderRadius: 10,
                    border: `1.5px solid ${rv.estado === 'ok' ? '#b7eb8f' : rv.estado === 'nf' ? '#ffa39e' : '#e0e0e0'}`,
                    background: rv.estado === 'ok' ? '#f6ffed' : rv.estado === 'nf' ? '#fff1f0' : '#fafafa',
                  }}>
                    <div style={{ fontSize: 13, color: '#2c3e50', marginBottom: 10, fontWeight: 500 }}>{item.descripcion}</div>
                    {already_signed ? (
                      <span style={{
                        background: rv.estado === 'ok' ? '#27ae60' : rv.estado === 'nf' ? '#e74c3c' : '#95a5a6',
                        color: '#fff', borderRadius: 6, padding: '3px 14px', fontSize: 13, fontWeight: 700,
                      }}>
                        {rv.estado.toUpperCase()}
                      </span>
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {ESTADOS.map(e => (
                          <button
                            key={e.key}
                            onClick={() => setEstado(item.id, e.key)}
                            style={{
                              flex: 1, padding: '10px 0', borderRadius: 8, fontWeight: 700, fontSize: 15,
                              cursor: 'pointer', border: '2px solid',
                              borderColor: rv.estado === e.key ? e.bg : '#dee2e6',
                              background: rv.estado === e.key ? e.bg : '#fff',
                              color: rv.estado === e.key ? e.color : '#636e72',
                              transition: 'all 0.15s',
                            }}
                          >
                            {e.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {rv.estado === 'nf' && !already_signed && (
                      <textarea
                        placeholder="Describir el problema (opcional)..."
                        value={rv.observacion}
                        onChange={e => setObs(item.id, e.target.value)}
                        style={{
                          width: '100%', marginTop: 8, padding: '8px 10px', borderRadius: 8,
                          border: '1px solid #ffa39e', fontSize: 12, resize: 'vertical',
                          minHeight: 56, boxSizing: 'border-box', fontFamily: 'Arial, sans-serif',
                        }}
                      />
                    )}
                    {rv.observacion && already_signed && (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#7f8c8d' }}>Obs: {rv.observacion}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Firma */}
        {already_signed ? (
          <div style={{ ...s.card, textAlign: 'center' }}>
            <p style={{ color: '#27ae60', fontWeight: 700, fontSize: 15 }}>
              ✔ Checklist firmado por {checklist.firmado_por}
            </p>
            {checklist.firma_data && (
              <img src={checklist.firma_data} alt="Firma"
                style={{ maxWidth: 280, border: '1px solid #dee2e6', borderRadius: 8, marginTop: 8 }} />
            )}
          </div>
        ) : (
          <div style={s.card}>
            <div style={s.secTitle}>Firma de conformidad</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ ...s.label, display: 'block', marginBottom: 6 }}>Nombre completo</label>
              <input
                type="text" value={firmadoPor} onChange={e => setFirmadoPor(e.target.value)}
                placeholder="Ingrese su nombre"
                style={{ width: '100%', padding: '11px 14px', borderRadius: 8, border: '1px solid #ced4da', fontSize: 15, boxSizing: 'border-box' }}
              />
            </div>

            {/* Preview de firma o placeholder */}
            <div style={{
              border: '2px dashed #ced4da', borderRadius: 12, background: '#fafafa',
              minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 14, overflow: 'hidden',
            }}>
              {firmaPreview
                ? <img src={firmaPreview} alt="Firma" style={{ maxHeight: 120, maxWidth: '100%' }} />
                : <span style={{ color: '#adb5bd', fontSize: 14 }}>Su firma aparecerá aquí</span>
              }
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => setModalFirma(true)}
                style={{
                  padding: '13px', borderRadius: 8, border: '2px solid #2c3e6e',
                  background: '#fff', color: '#2c3e6e', fontWeight: 700, fontSize: 15, cursor: 'pointer',
                }}
              >
                ✍️ {firmaPreview ? 'Volver a firmar' : 'Dibujar firma'}
              </button>
              {firmaPreview && (
                <button
                  onClick={enviar} disabled={saving}
                  style={{
                    padding: '13px', borderRadius: 8, border: 'none',
                    background: saving ? '#bdc3c7' : '#2c3e6e', color: '#fff',
                    fontWeight: 700, fontSize: 15, cursor: saving ? 'default' : 'pointer',
                  }}
                >
                  {saving ? 'Guardando...' : '✅ Confirmar y enviar'}
                </button>
              )}
            </div>

            <FirmaCanvasModal
              open={modalFirma}
              onClose={() => setModalFirma(false)}
              onConfirm={(b64) => { setFirmaPreview(b64); setModalFirma(false); }}
              titulo="Dibuje su firma"
            />
          </div>
        )}
      </div>
    </div>
  );
}
